import styles from './index.module.scss';
import React from 'react';
import { Button, Modal, Empty } from '@maxtropy/components';
import classNames from 'classnames';
import { V2LightmapSunlightDevicePagePostResponse } from '@maxtropy/device-customer-apis-v2';
import { DoubleRightOutlined } from '@ant-design/icons';
import { Spin } from 'antd';

export type SunLightPopupData = Exclude<V2LightmapSunlightDevicePagePostResponse['list'], undefined>[number];

interface SunLightModalProps {
  open: boolean;
  loading: boolean;
  title?: string;
  data?: SunLightPopupData;
  onClose: () => void;
  onOpenHistory: () => void;
}

const getSunLightStatusMeta = (status?: number) => {
  if (status === 1) {
    return { text: '开启', className: styles.sunStatusOpen };
  }
  if (status === 0) {
    return { text: '关闭', className: styles.sunStatusClose };
  }
  if (status === 2) {
    return { text: '离线', className: styles.sunStatusOffline };
  }
  return { text: '-', className: styles.sunStatusUnknown };
};

const getModalContainer = () => document.getElementById('full_screen_box') ?? document.body;

const SunLightModal: React.FC<SunLightModalProps> = ({ open, loading, title, data, onClose, onOpenHistory }) => {
  const sunLightStatusMeta = getSunLightStatusMeta(data?.intStatus);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={360}
      footer={null}
      title={
        <div className={styles.lightModalTitle}>
          <span>{data?.deviceName ?? title ?? '太阳能路灯'}</span>
          {data?.deviceId ? (
            <Button type="link" onClick={onOpenHistory}>
              更多
              <DoubleRightOutlined />
            </Button>
          ) : null}
        </div>
      }
      className={styles.lightModal}
      destroyOnClose
      getContainer={getModalContainer}
    >
      {loading ? (
        <div className={styles.lightModalState}>
          <Spin />
        </div>
      ) : data ? (
        <div className={styles.sunLightModalContent}>
          <div className={styles.sunMetric}>
            <span className={styles.lightLabel}>状态：</span>
            <span className={classNames(styles.sunStatus, sunLightStatusMeta.className)}>
              {sunLightStatusMeta.text}
            </span>
          </div>
          <div className={styles.sunMetric}>
            <span className={styles.lightLabel}>温度：</span>
            <span className={styles.lightText}>{data.temperature ?? '--'}℃</span>
          </div>
          <div className={styles.sunMetric}>
            <span className={styles.lightLabel}>电池电量：</span>
            <span className={styles.lightText}>{data.batteryCapacity ?? '--'}kWh</span>
          </div>
          <div className={styles.sunMetric}>
            <span className={styles.lightLabel}>太阳板电压：</span>
            <span className={styles.lightText}>{data.solarPanelVoltage ?? '--'}V</span>
          </div>
          <div className={styles.sunMetric}>
            <span className={styles.lightLabel}>蓄电池电压：</span>
            <span className={styles.lightText}>{data.electricityVoltage ?? '--'}V</span>
          </div>
          <div className={styles.sunMetricFull}>
            <span className={styles.lightLabel}>放电电压：</span>
            <span className={styles.sunDischargeValue}>{data.dischargeVoltage ?? '--'}V</span>
          </div>
          <div className={styles.sunMetric}>
            <span className={styles.lightLabel}>充电电流：</span>
            <span className={styles.sunChargeValue}>{data.chargingCurrent ?? '--'}A</span>
          </div>
          <div className={styles.sunMetric}>
            <span className={styles.lightLabel}>放电电流：</span>
            <span className={styles.sunDischargeValue}>{data.dischargeCurrent ?? '--'}A</span>
          </div>
          <div className={styles.sunMetric}>
            <span className={styles.lightLabel}>总充电安时数：</span>
            <span className={styles.sunChargeValue}>{data.totalChargingAmpereHours ?? '--'}h</span>
          </div>
          <div className={styles.sunMetric}>
            <span className={styles.lightLabel}>总放电安时数：</span>
            <span className={styles.sunDischargeValue}>{data.totalDischargingAmpereHours ?? '--'}h</span>
          </div>
        </div>
      ) : (
        <div className={styles.lightModalState}>
          <Empty description="暂无数据" />
        </div>
      )}
    </Modal>
  );
};

export default SunLightModal;
